import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

// Feature 41 — buy / hold / sell meter for the S&P 500, from GET /market/timing-meter.

const ZONES = [
  { key: 'SELL', label: 'Sell', color: '#C62828' },
  { key: 'CAUTION', label: 'Caution', color: '#EF6C00' },
  { key: 'HOLD', label: 'Hold', color: '#1565C0' },
  { key: 'BUY', label: 'Buy', color: '#2E7D32' },
];

const pct = (v: number | null | undefined, digits = 0) =>
  v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
const signed = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(digits)}%`;

function Gauge({ state }: { state: string }) {
  // Semicircle split into four equal arcs; the needle points at the middle of the active one.
  const idx = Math.max(0, ZONES.findIndex(z => z.key === state));
  const cx = 120, cy = 110, r = 90, w = 22;
  const arc = (i: number) => {
    const a0 = Math.PI - (i * Math.PI) / 4, a1 = Math.PI - ((i + 1) * Math.PI) / 4;
    const p = (a: number, rr: number) => `${cx + rr * Math.cos(a)} ${cy - rr * Math.sin(a)}`;
    return `M ${p(a0, r)} A ${r} ${r} 0 0 1 ${p(a1, r)} L ${p(a1, r - w)} A ${r - w} ${r - w} 0 0 0 ${p(a0, r - w)} Z`;
  };
  const needle = Math.PI - ((idx + 0.5) * Math.PI) / 4;
  return (
    <svg viewBox="0 0 240 130" width="100%" style={{ maxWidth: 300 }} role="img" aria-label={`Meter: ${state}`}>
      {ZONES.map((z, i) => (
        <path key={z.key} d={arc(i)} fill={z.color} opacity={i === idx ? 1 : 0.28} />
      ))}
      {ZONES.map((z, i) => {
        const a = Math.PI - ((i + 0.5) * Math.PI) / 4;
        return (
          <text key={z.key} x={cx + (r + 14) * Math.cos(a)} y={cy - (r + 14) * Math.sin(a) + 4}
                textAnchor="middle" fontSize="11" fontWeight={i === idx ? 800 : 500} fill="currentColor">
            {z.label}
          </text>
        );
      })}
      <line x1={cx} y1={cy} x2={cx + (r - 30) * Math.cos(needle)} y2={cy - (r - 30) * Math.sin(needle)}
            stroke="currentColor" strokeWidth={4} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={7} fill="currentColor" />
    </svg>
  );
}

export default function TimingMeter({ meter }: { meter: any }) {
  if (!meter) return null;
  const zone = ZONES.find(z => z.key === meter.state) ?? ZONES[2];
  const inp = meter.inputs ?? {};
  const sp = meter.spell ?? {};
  const ab = meter.after_buys ?? {};
  const lights = meter.lights ?? {};
  const strat = meter.strategies ?? {};
  const pub = meter.since_published?.growth_trend;
  const tableRows: [string, string, any][] = [
    ['Buy & hold', 'always in stocks', strat.buy_and_hold],
    ['This meter (growth-trend)', 'sell only when trend is down and unemployment rising', strat.growth_trend],
    ['Trend only', 'sell whenever the S&P is below its 10-month average', strat.trend],
    ['Sell on 5+ warning lights', '', strat.lights_5],
  ];

  return (
    <Paper sx={{ p: 3, mb: 3, borderLeft: `6px solid ${zone.color}` }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} sx={{ textAlign: 'center', color: 'text.primary' }}>
          <Gauge state={meter.state} />
          <Typography variant="h5" fontWeight={800} sx={{ color: zone.color, mt: 0.5 }}>
            {zone.label.toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{meter.headline}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            S&amp;P 500 (an index fund such as SPY or VOO) · as of {meter.as_of}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>How long to hold</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            There is no fixed holding period: <strong>hold until the sell rule fires.</strong> {meter.sell_rule}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The rule has said "in stocks" since <strong>{sp.since}</strong> ({sp.months} months). Its {sp.count} past
            in-market spells lasted a median <strong>{sp.historical_median_months} months</strong> (middle half{' '}
            {sp.historical_p25_months}–{sp.historical_p75_months}); {pct(sp.share_spells_gaining)} ended higher than they began.
          </Typography>
          <Typography variant="body2">
            After its past buy signals the S&amp;P returned a median{' '}
            <strong>{signed(ab['12m']?.median, 0)}</strong> over 12 months ({pct(ab['12m']?.share_positive)} positive) and{' '}
            <strong>{signed(ab['24m']?.median, 0)}</strong> over 24 months ({pct(ab['24m']?.share_positive)} positive).
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>Today's inputs</Typography>
          {[
            ['S&P vs its 10-month average', signed(inp.spx_vs_avg10m), inp.trend_down ? 'below — trend down' : 'above — trend up', inp.trend_down],
            ['Unemployment vs its 12-month average', `${inp.unemployment?.toFixed(1)}% vs ${inp.unemployment_avg12m?.toFixed(2)}%`,
              `${inp.unemp_rising ? 'rising' : 'not rising'} (${inp.unemployment_month} data)`, inp.unemp_rising],
            ['Below the all-time high', signed(inp.drawdown_from_high), inp.drawdown_from_high <= -0.2 ? 'a 20%+ dip' : '', false],
          ].map(([label, value, note, bad]) => (
            <Box key={label as string} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: bad ? 'error.main' : 'text.primary' }}>
                {value} <Typography component="span" variant="caption" color="text.secondary">{note}</Typography>
              </Typography>
            </Box>
          ))}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Collapse warning lights: {lights.count} of {lights.all?.length ?? 12} lit
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {(lights.all ?? []).map((l: any) => (
              <Tooltip key={l.key} title={`Data for ${l.month}`}>
                <Chip size="small" label={l.name}
                      sx={l.on
                        ? { bgcolor: '#C62828', color: '#FFFFFF', fontWeight: 700 }
                        : { bgcolor: 'action.hover', color: 'text.secondary' }} />
              </Tooltip>
            ))}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lights lit</TableCell>
                <TableCell align="right">Months</TableCell>
                <TableCell align="right">20%+ fall within 12 mo</TableCell>
                <TableCell align="right">10%+ fall within 6 mo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(lights.table ?? []).map((b: any) => (
                <TableRow key={b.lights} selected={b.lights === lights.odds?.lights}>
                  <TableCell>{b.lights}</TableCell>
                  <TableCell align="right">{b.months}</TableCell>
                  <TableCell align="right">{pct(b.crash12)}</TableCell>
                  <TableCell align="right">{pct(b.fall6)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Every month since {lights.since}. Five or more lights tripled the chance of a crash, but they stay lit into
            the bottom, so selling on them did not beat holding (table on the right). Read them as a caution, not a sell.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>Track record, {strat.buy_and_hold?.from}–{strat.buy_and_hold?.to}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell align="right">Yearly return</TableCell>
                <TableCell align="right">Worst fall</TableCell>
                <TableCell align="right">Time in stocks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.filter(([, , s]) => s).map(([name, desc, s]) => (
                <TableRow key={name}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={name.startsWith('This') ? 700 : 400}>{name}</Typography>
                    {desc && <Typography variant="caption" color="text.secondary">{desc}</Typography>}
                  </TableCell>
                  <TableCell align="right">{pct(s.cagr, 1)}</TableCell>
                  <TableCell align="right">{pct(s.max_dd)}</TableCell>
                  <TableCell align="right">{pct(s.invested)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {meter.dial && (
            <Alert severity={meter.dial.exposure_pct ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
              <strong>Exposure dial: {meter.dial.exposure_pct}% in the S&amp;P 500.</strong> Holding less when the market
              swings hard (volatility targeting) was tested from {meter.dial.from?.slice(0, 4)} on total returns and did
              not help: Sharpe {meter.dial.rows['vol target 15%']?.sharpe?.toFixed(2)} against{' '}
              {meter.dial.rows['buy & hold']?.sharpe?.toFixed(2)} for buy &amp; hold, and{' '}
              {meter.dial.rows['growth-trend × vol 15%']?.sharpe?.toFixed(2)} combined with this rule against{' '}
              {meter.dial.rows['growth-trend']?.sharpe?.toFixed(2)} for the rule alone. So the dial is all in or all
              out, by this rule: {meter.dial.rows['growth-trend']?.cagr_pct?.toFixed(1)}% a year with a worst fall of{' '}
              {meter.dial.rows['growth-trend']?.worst_fall_pct?.toFixed(0)}%, against{' '}
              {meter.dial.rows['buy & hold']?.cagr_pct?.toFixed(1)}% and {meter.dial.rows['buy & hold']?.worst_fall_pct?.toFixed(0)}%.
            </Alert>
          )}
          {pub && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Since the rule was published ({pub.published}) it returned {pct(pub.rule.cagr, 1)} a year vs{' '}
              {pct(pub.buy_and_hold.cagr, 1)} for buy &amp; hold, with the same worst fall ({pct(pub.rule.max_dd)}). Its
              value is avoiding long recession bear markets such as 2008; it cannot time sharp drops like 2020 or 2022.
            </Alert>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}
